"use client";

import {
  useRef,
  useEffect,
  useImperativeHandle,
  type RefObject,
  type FC,
  type Ref,
} from "react";
import {
  Button,
  Card,
  Flex,
  type FormFieldSchema,
  mergeRefs,
  ProgressBar,
  Slides,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import { MaximumMortgageWizardProvider } from "../MaximumMortgageWizardProvider";
import { useMaximumMortgageWizard } from "../../hooks/useMaximumMortgageWizard";
import type { NewHouseSlideProps } from "./components/NewHouseSlide";
import type {
  MaximumMortgageWizardFormValues,
  MaximumMortgageWizardState,
  MaximumMortgageWizardSlide,
  MaximumMortgageFlowConfig,
} from "../../types/MaximumMortgageWizard";
import type { Bank } from "../../types/Bank";

import { footerStyle } from "./MaximumMortgageWizard.css";

export type MaximumMortgageWizardHandle = {
  /**
   * Imperatively update wizard form values from outside the wizard context.
   */
  updateFormValues: (values: Partial<MaximumMortgageWizardFormValues>) => void;
  /**
   * Advance to the next wizard step WITH validation.
   * On the last step, this triggers submit (calculate).
   * Used when the user clicks "Volgende stap".
   */
  nextStep: () => void;
  /**
   * Advance to the next wizard step WITHOUT validation.
   *
   * ⚠️ WARNING: This bypasses form validation entirely.
   * Only use for agent-driven navigation via inputUpdate.
   *
   * Why: The agent fills form values via updateFormValues() which triggers
   * async React state updates. If nextStep() calls requestSubmit()
   * immediately after, the form validates against stale (pre-update)
   * values and rejects the advance. The agent prompt instructs it to
   * fill all required fields before advancing — prompt-level guidance
   * is the safety net, not HTML form validation.
   */
  navigateToNext: () => void;
  /**
   * Go back to the previous wizard step.
   * Used for agent-driven navigation via inputUpdate (previousStep).
   */
  navigateToPrevious: () => void;
};

export type MaximumMortgageWizardProps = {
  /**
   * Whether the wizard is in a loading state
   */
  loading?: boolean;
  /**
   * Initial form values to populate the wizard (takes precedence over localStorage)
   */
  initialValues?: Partial<MaximumMortgageWizardFormValues>;
  /**
   * Fetches address search results for the new house slide autocomplete
   */
  getSearchResults?: NewHouseSlideProps["getSearchResults"];
  /**
   * Fetches a full listing by address object ID for the new house slide
   */
  getListing?: NewHouseSlideProps["getListing"];
  /**
   * Called when the wizard is submitted with the current form values
   */
  onSubmit?: (
    formValues: Partial<MaximumMortgageWizardFormValues>,
  ) => void | Promise<void>;
  /**
   * Called whenever a form value changes during the wizard flow
   */
  onChange?: (formValues: Partial<MaximumMortgageWizardFormValues>) => void;
  /**
   * Called when the wizard navigation state changes (e.g. slide transitions)
   */
  onStateChange?: (state: MaximumMortgageWizardState) => void;
  /**
   * Pre-selected address object ID to auto-populate the new house slide
   */
  addressObjectID?: string;
  /**
   * Backend-driven flow configuration. When present, restricts
   * which mortgage types are available in the wizard.
   */
  flowConfig?: MaximumMortgageFlowConfig;
  /**
   * Async function that resolves to a list of banks/lenders.
   * When provided, the lender dropdown in PurposeSlide and CurrentHouseSlide
   * will be populated with the returned banks plus an "other" option.
   */
  getBanks?: () => Promise<Bank[]>;
  /**
   * Called when the enriched field schema changes — on field registration,
   * deregistration, and value changes. Provides the full schema with
   * fieldKey, label, type, step, and current value for AI agent awareness.
   */
  onFieldSchemaChange?: (
    slideKey: MaximumMortgageWizardSlide,
    schema: FormFieldSchema[],
  ) => void;
  /**
   * Ref that exposes imperative methods (e.g. `updateFormValues`)
   * so the parent can programmatically update wizard state.
   */
  wizardRef?: RefObject<MaximumMortgageWizardHandle | null>;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * Maximum Mortgage Wizard component with multi-step form
 * Provides a guided experience for mortgage calculation
 */
export const MaximumMortgageWizard: FC<MaximumMortgageWizardProps> = ({
  loading,
  initialValues,
  onChange,
  onStateChange,
  onSubmit,
  onFieldSchemaChange,
  wizardRef,
  getSearchResults,
  getListing,
  addressObjectID,
  flowConfig,
  getBanks,
  ref,
}) => (
  <MaximumMortgageWizardProvider
    onChange={onChange}
    onStateChange={onStateChange}
    onSubmit={onSubmit}
    onFieldSchemaChange={onFieldSchemaChange}
    getSearchResults={getSearchResults}
    getListing={getListing}
    loading={loading}
    initialValues={initialValues}
    addressObjectID={addressObjectID}
    flowConfig={flowConfig}
    getBanks={getBanks}
  >
    <WizardContent ref={ref} wizardRef={wizardRef} />
  </MaximumMortgageWizardProvider>
);

type WizardContentProps = {
  /**
   * Ref that exposes imperative wizard methods via `useImperativeHandle`.
   */
  wizardRef?: RefObject<MaximumMortgageWizardHandle | null>;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * Internal wizard component that uses the context
 */
const WizardContent: FC<WizardContentProps> = ({ wizardRef, ref }) => {
  const { t } = useTranslation();
  const {
    state,
    dispatch,
    slideOrder,
    currentSlideIndex,
    slides,
    slidesRef,
    loading,
    handleNext,
    handlePrevious,
    handleSubmit,
    updateFormValues,
    navigateToNext,
  } = useMaximumMortgageWizard();

  const isLastSlide = currentSlideIndex === slideOrder.length - 1;

  useImperativeHandle(
    wizardRef,
    () => ({
      updateFormValues,
      nextStep: () => {
        if (isLastSlide) {
          handleSubmit();
        } else {
          handleNext();
        }
      },
      navigateToNext,
      navigateToPrevious: handlePrevious,
    }),
    [
      updateFormValues,
      handleNext,
      handleSubmit,
      isLastSlide,
      navigateToNext,
      handlePrevious,
    ],
  );

  const cardRef = useRef<HTMLDivElement>(null);
  const canScrollRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      canScrollRef.current = true;
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  const handleSlideChange = (slideKey: string) => {
    if (slideKey !== state.currentSlide) {
      dispatch({
        type: "SET_SLIDE",
        payload: slideKey as MaximumMortgageWizardSlide,
      });
    }

    if (!canScrollRef.current) {
      return;
    }

    if (cardRef.current) {
      const elementPosition = cardRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <Card
      ref={mergeRefs(cardRef, ref)}
      border="default"
      applyPadding={false}
      overflowHidden
      leading={
        loading && <ProgressBar position="absolute" variant="infinite" />
      }
    >
      <Slides
        ref={slidesRef}
        slides={slides}
        initialSlide={state.currentSlide}
        onSlideChange={handleSlideChange}
      />

      <Flex gap="xs" justify="flex-end" className={footerStyle}>
        {currentSlideIndex > 0 && (
          <Button
            variant="opaque"
            border="default"
            onClick={handlePrevious}
            disabled={loading}
          >
            {t("maxMortgageWizard.buttons.previous")}
          </Button>
        )}

        {isLastSlide ? (
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {t("maxMortgageWizard.buttons.calculate")}
          </Button>
        ) : (
          <Button variant="primary" onClick={handleNext} disabled={loading}>
            {t("maxMortgageWizard.buttons.nextStep")}
          </Button>
        )}
      </Flex>
    </Card>
  );
};
